import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, Loader2, Store, XCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { toPublicStorageUrl } from '../../../lib/url';
import { errorMessage, reportError } from '../../../lib/errors';
import { formatJobNumber } from '../../../lib/docNumbers';
import { DEFAULT_DOCUMENT_ACCENT } from '../../../lib/brandAccent';

/**
 * The customer's side of additional work: what was found, what it costs, what
 * the job will cost if they say yes, and two buttons.
 *
 * Reached through the same /quote/<token> link as a quote (PublicQuoteView
 * hands over when the token is additional work). No account. Every decision
 * goes through fn_accept_quote_publicly / fn_reject_quote_publicly, which lock
 * the row, refuse a stale or superseded link with a sentence written for the
 * customer, and treat a second click as the same answer -- so this page
 * re-reads the request after deciding rather than trusting its own state.
 */

interface Line { type: 'part' | 'labour'; description: string; quantity: number; unitPrice: number }

export interface AdditionalWorkPayload {
    quote: {
        state: 'awaiting_customer' | 'accepted' | 'declined' | 'not_current';
        state_message: string | null;
        decided_at: string | null;
        total_amount: number;
        sequence_no: number;
        job_number: number | null;
        original_amount: number | null;
        previously_accepted_additions: number;
        photo_count: number;
        metadata: {
            lineItems: Line[];
            customer: { name?: string };
            vehicle: { vrm?: string; make?: string; model?: string; year?: number | null };
            notes: string | null;
        };
    };
    organization: {
        name: string;
        logo_url?: string;
        invoice_accent_color?: string;
        primary_color?: string;
    } | null;
}

// Messages the RPCs write for the customer; anything else is our fault.
const CUSTOMER_MESSAGE = /no longer current|already been accepted|declined|not been sent|expired|contact the workshop/i;


const money = (n: number | null | undefined) => `£${(Number(n) || 0).toFixed(2)}`;

export default function AdditionalWorkApproval({ token, initial, decisionHint }: {
    token: string;
    initial: AdditionalWorkPayload;
    decisionHint: string | null;
}) {
    const [data, setData] = useState(initial);
    const [photos, setPhotos] = useState<string[]>([]);
    const [busy, setBusy] = useState<null | 'accept' | 'decline'>(null);
    const [declining, setDeclining] = useState(decisionHint === 'decline');
    const [reason, setReason] = useState('');
    const [actionError, setActionError] = useState<string | null>(null);

    const { quote, organization } = data;
    const accent = organization?.invoice_accent_color || organization?.primary_color || DEFAULT_DOCUMENT_ACCENT;
    const vehicle = quote.metadata.vehicle || {};
    const vehicleName = [vehicle.make, vehicle.model].filter(Boolean).join(' ') || 'your vehicle';
    const agreed = Number(quote.original_amount || 0) + Number(quote.previously_accepted_additions || 0);
    const hasAgreed = quote.original_amount != null || Number(quote.previously_accepted_additions) > 0;

    useEffect(() => {
        if (!quote.photo_count) return;
        supabase.functions.invoke('get-additional-work-evidence', { body: { token } }).then(({ data: body, error }) => {
            if (error) { reportError(error, 'additional-work-approval.get-additional-work-evidence'); return; }
            setPhotos(((body as { photos?: { url: string }[] } | null)?.photos ?? []).map((p) => toPublicStorageUrl(p.url)));
        });
    }, [token, quote.photo_count]);

    const reload = async () => {
        const { data: fresh, error } = await supabase.rpc('fn_get_quote_by_token', { p_token: token });
        if (error) { reportError(error, 'additional-work-approval.fn-get-quote-by-token'); return; }
        if (fresh) setData(fresh as AdditionalWorkPayload);
    };

    const decide = async (accept: boolean) => {
        setBusy(accept ? 'accept' : 'decline');
        setActionError(null);
        try {
            const { error } = accept
                ? await supabase.rpc('fn_accept_quote_publicly', { p_token: token, p_user_agent: navigator.userAgent })
                : await supabase.rpc('fn_reject_quote_publicly', { p_token: token, p_reason: reason.trim() || null, p_user_agent: navigator.userAgent });
            if (error) {
                const msg = errorMessage(error, '');
                if (!CUSTOMER_MESSAGE.test(msg)) reportError(error, accept ? 'additional-work-approval.accept' : 'additional-work-approval.decline');
                setActionError(CUSTOMER_MESSAGE.test(msg) ? msg : 'Your answer could not be saved. Please try again.');
            }
            await reload();
        } finally {
            setBusy(null);
        }
    };

    return (
        <div data-theme="paper" className="min-h-screen bg-raised pb-[calc(2rem+env(safe-area-inset-bottom))]">
            <header data-theme="dark" className="px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-4 border-b-4 bg-canvas" style={{ borderColor: accent }}>
                <div className="max-w-xl mx-auto flex items-center gap-3 min-w-0">
                    {organization?.logo_url ? (
                        <img data-theme="paper" src={organization.logo_url} alt="" className="h-10 max-w-[40%] object-contain bg-card rounded p-1" />
                    ) : (
                        <div className="w-10 h-10 shrink-0 bg-tint/10 rounded grid place-items-center"><Store className="w-5 h-5 text-fg" /></div>
                    )}
                    <div className="min-w-0">
                        <p className="font-bold text-fg truncate">{organization?.name}</p>
                        <p className="text-xs text-fg/70 truncate">
                            Extra work #{quote.sequence_no}{quote.job_number != null ? ` · ${formatJobNumber(quote.job_number)}` : ''}
                        </p>
                    </div>
                </div>
            </header>

            <main className="max-w-xl mx-auto px-4 py-5 space-y-4">
                <section className="bg-card rounded-xl shadow-sm p-5">
                    <p className="text-xs font-bold uppercase tracking-wider text-fg-4">
                        {vehicle.vrm && <span className="fd-plate mr-2">{vehicle.vrm}</span>}
                        {vehicleName}
                    </p>
                    <h1 className="mt-3 text-xl font-bold leading-snug">Additional work found on your {vehicleName}</h1>
                    <p className="mt-2 text-sm text-fg-2 leading-relaxed">
                        {organization?.name || 'The workshop'} found work that wasn&apos;t in the original quote.
                        Nothing extra is done unless you approve it.
                    </p>
                    {quote.metadata.notes && (
                        <p className="mt-4 text-sm text-fg whitespace-pre-line border-l-4 pl-3 py-1" style={{ borderColor: accent }}>
                            {quote.metadata.notes}
                        </p>
                    )}
                </section>

                {photos.length > 0 && (
                    <section className="bg-card rounded-xl shadow-sm p-3">
                        <div className="grid grid-cols-2 gap-2">
                            {photos.map((url, i) => (
                                <a key={url} href={url} target="_blank" rel="noreferrer" className="block aspect-square rounded-lg overflow-hidden bg-raised">
                                    <img src={url} alt={`Photo ${i + 1} of what was found`} className="w-full h-full object-cover" />
                                </a>
                            ))}
                        </div>
                    </section>
                )}

                <section className="bg-card rounded-xl shadow-sm p-5">
                    <ul className="divide-y divide-line">
                        {quote.metadata.lineItems.map((l, i) => (
                            <li key={i} className="py-3 flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-fg break-words">{l.description}</p>
                                    <p className="text-xs text-fg-4 capitalize">{l.type}{Number(l.quantity) !== 1 ? ` · ${l.quantity} × ${money(l.unitPrice)}` : ''}</p>
                                </div>
                                <span className="text-sm font-mono font-semibold text-fg shrink-0">{money(Number(l.quantity) * Number(l.unitPrice))}</span>
                            </li>
                        ))}
                    </ul>
                    <dl className="mt-3 pt-3 border-t border-line space-y-1.5">
                        <div className="flex justify-between gap-3">
                            <dt className="text-sm font-semibold text-fg">Additional cost</dt>
                            <dd className="text-lg font-bold font-mono" style={{ color: accent }}>{money(quote.total_amount)}</dd>
                        </div>
                        {hasAgreed && (
                            <>
                                <div className="flex justify-between gap-3 text-sm text-fg-3">
                                    <dt>Already agreed</dt><dd className="font-mono">{money(agreed)}</dd>
                                </div>
                                <div className="flex justify-between gap-3 text-sm text-fg font-semibold">
                                    <dt>New total if you approve</dt><dd className="font-mono">{money(agreed + Number(quote.total_amount))}</dd>
                                </div>
                            </>
                        )}
                    </dl>
                </section>

                <Decision
                    state={quote.state}
                    stateMessage={quote.state_message}
                    decidedAt={quote.decided_at}
                    accent={accent}
                    busy={busy}
                    declining={declining}
                    setDeclining={setDeclining}
                    reason={reason}
                    setReason={setReason}
                    onAccept={() => decide(true)}
                    onDecline={() => decide(false)}
                    actionError={actionError}
                />
            </main>
        </div>
    );
}

function Decision(props: {
    state: AdditionalWorkPayload['quote']['state'];
    stateMessage: string | null;
    decidedAt: string | null;
    accent: string;
    busy: null | 'accept' | 'decline';
    declining: boolean;
    setDeclining: (v: boolean) => void;
    reason: string;
    setReason: (v: string) => void;
    onAccept: () => void;
    onDecline: () => void;
    actionError: string | null;
}) {
    const when = props.decidedAt
        ? new Date(props.decidedAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
        : null;

    if (props.state === 'accepted' || props.state === 'declined') {
        const accepted = props.state === 'accepted';
        return (
            <section role="status" className={`rounded-xl p-5 flex items-start gap-3 ${accepted ? 'bg-success-fill text-on-fill' : 'bg-pressed text-fg'}`}>
                {accepted ? <CheckCircle className="w-7 h-7 shrink-0" /> : <XCircle className="w-7 h-7 shrink-0 text-danger" />}
                <div>
                    <h2 className="font-bold text-lg text-inherit">{accepted ? 'Additional work accepted' : 'Additional work declined'}</h2>
                    <p className="text-sm opacity-90">
                        {when ? `Recorded ${when}. ` : ''}The workshop has been told.
                    </p>
                </div>
            </section>
        );
    }

    if (props.state === 'not_current') {
        return (
            <section role="status" className="rounded-xl p-5 bg-gold/10 border border-gold flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-gold shrink-0" />
                <p className="text-sm text-gold">{props.stateMessage || 'This request is no longer current.'}</p>
            </section>
        );
    }

    return (
        <section className="bg-card rounded-xl shadow-sm p-5">
            {props.actionError && (
                <p role="alert" className="mb-3 text-sm text-danger bg-danger/10 border border-danger rounded-lg px-3 py-2">{props.actionError}</p>
            )}
            {props.declining ? (
                <>
                    <h2 className="font-bold">Decline the additional work</h2>
                    <p className="mt-1 text-sm text-fg-3">The original work goes ahead as agreed. A reason is optional.</p>
                    <textarea
                        value={props.reason}
                        onChange={(e) => props.setReason(e.target.value)}
                        maxLength={1000}
                        rows={3}
                        placeholder="Optional: why (price, timing, I'll get it done elsewhere…)"
                        className="mt-3 w-full border border-line-strong rounded-lg px-3 py-2 text-sm text-fg placeholder:text-fg-4 focus:outline-none focus:border-line-glow"
                    />
                    <button
                        type="button"
                        onClick={props.onDecline}
                        disabled={!!props.busy}
                        className="mt-3 w-full min-h-[52px] rounded-lg bg-danger-fill text-on-fill font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {props.busy === 'decline' ? <Loader2 className="w-5 h-5 animate-spin" /> : <XCircle className="w-5 h-5" />}
                        Decline additional work
                    </button>
                    <button
                        type="button"
                        onClick={() => props.setDeclining(false)}
                        disabled={!!props.busy}
                        className="mt-2 w-full min-h-[44px] rounded-lg border border-line-strong text-fg-2 font-medium text-sm"
                    >
                        Back
                    </button>
                </>
            ) : (
                <>
                    <button
                        type="button"
                        onClick={props.onAccept}
                        disabled={!!props.busy}
                        className="w-full min-h-[56px] rounded-lg text-fg font-bold text-[1rem] flex items-center justify-center gap-2 disabled:opacity-50"
                        style={{ backgroundColor: props.accent }}
                    >
                        {props.busy === 'accept' ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                        Accept additional work
                    </button>
                    <button
                        type="button"
                        onClick={() => props.setDeclining(true)}
                        disabled={!!props.busy}
                        className="mt-3 w-full min-h-[48px] rounded-lg border border-line-strong text-fg font-semibold"
                    >
                        Decline additional work
                    </button>
                    <p className="mt-3 text-xs text-fg-4 text-center">Your answer is recorded with the date and time and sent to the workshop.</p>
                </>
            )}
        </section>
    );
}
